import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDateTime } from '../lib/format.js';
import { copyText } from '../lib/clipboard.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface TokenRow {
  id: string;
  name: string;
  tokenPrefix: string;
  role: 'VIEWER' | 'MEMBER' | 'ADMIN';
  lastUsedAt?: string;
  revokedAt?: string;
  createdAt: string;
}

/**
 * Server API tokens (`rok_…`) — programmatic access to the REST API, org-wide
 * and role-capped. Distinct from the SDK ingest keys above (which are public
 * by design); a server token is a secret and its raw value is shown exactly
 * once, at creation.
 */
type SnippetLang = 'curl' | 'js' | 'python' | 'mcp';

/** Ready-to-paste examples; a freshly minted token is embedded directly. */
function snippet(lang: SnippetLang, origin: string, orgId: string, token: string | null): string {
  const tok = token ?? 'rok_YOUR_TOKEN';
  if (lang === 'curl') {
    return [
      `export REPLAYOPS_TOKEN="${tok}"`,
      ``,
      `# Sessions with errors, newest first`,
      `curl -s "${origin}/v1/orgs/${orgId}/sessions?hasError=true&limit=20" \\`,
      `  -H "Authorization: Bearer $REPLAYOPS_TOKEN"`,
      ``,
      `# Most frustrated sessions`,
      `curl -s "${origin}/v1/orgs/${orgId}/sessions?sortBy=frustrationScore&sortDir=desc&limit=10" \\`,
      `  -H "Authorization: Bearer $REPLAYOPS_TOKEN"`,
    ].join('\n');
  }
  if (lang === 'js') {
    return [
      `const REPLAYOPS_TOKEN = process.env.REPLAYOPS_TOKEN; // ${tok.slice(0, 12)}…`,
      ``,
      `const res = await fetch(`,
      `  '${origin}/v1/orgs/${orgId}/sessions?hasError=true&limit=20',`,
      `  { headers: { Authorization: \`Bearer \${REPLAYOPS_TOKEN}\` } },`,
      `);`,
      `const { data: sessions } = await res.json();`,
      `console.log(sessions.map((s) => \`\${s.id} \${s.entryUrl} errors=\${s.errorCount}\`));`,
    ].join('\n');
  }
  if (lang === 'mcp') {
    // Claude Desktop's config shape, which is also what Cursor and the other
    // clients read. mcp-remote is the bridge every client needs today: they
    // speak stdio locally and this endpoint speaks HTTP.
    return [
      `// claude_desktop_config.json. Settings ▸ Developer ▸ Edit Config`,
      `{`,
      `  "mcpServers": {`,
      `    "replayops": {`,
      `      "command": "npx",`,
      `      "args": [`,
      `        "-y", "mcp-remote", "${origin}/mcp",`,
      `        "--header", "Authorization: Bearer ${tok}"`,
      `      ]`,
      `    }`,
      `  }`,
      `}`,
      ``,
      `// Then ask: "which sessions had the most trouble this week?"`,
      `// The token carries the organization and the role, a VIEWER token`,
      `// can read everything and change nothing.`,
    ].join('\n');
  }
  return [
    `import os, requests`,
    ``,
    `token = os.environ["REPLAYOPS_TOKEN"]  # ${tok.slice(0, 12)}…`,
    `r = requests.get(`,
    `    "${origin}/v1/orgs/${orgId}/sessions",`,
    `    params={"hasError": "true", "limit": 20},`,
    `    headers={"Authorization": f"Bearer {token}"},`,
    `)`,
    `for s in r.json()["data"]:`,
    `    print(s["id"], s["entryUrl"], s["errorCount"])`,
  ].join('\n');
}

export function ApiTokensSection({ orgId }: { orgId?: string }) {
  const c = useT().integrations.tokens;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [role, setRole] = useState<TokenRow['role']>('VIEWER');
  const [minted, setMinted] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [snippetCopied, setSnippetCopied] = useState(false);

  const base = `/v1/orgs/${orgId}/api-tokens`;
  const tokens = useQuery({
    enabled: !!orgId,
    queryKey: ['api-tokens', orgId],
    queryFn: async () => (await api.get(base)).data.data as TokenRow[],
  });

  const create = useMutation({
    mutationFn: async () => (await api.post(base, { name, role })).data.data as TokenRow & { token: string },
    onSuccess: (d) => {
      setMinted(d.token);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-tokens', orgId] });
    },
  });

  /**
   * Mints a token for the examples and fills it in.
   *
   * The snippets can only ever show a token that was created in this browser
   * session: the raw value is hashed on the way in and shown once, so an
   * existing token cannot be put back into the box by anyone, including us.
   * Rather than leaving a placeholder to be hand-edited, this makes the real
   * one a click away.
   */
  const fillExample = useMutation({
    mutationFn: async () =>
      (await api.post(base, { name: 'Quick start', role })).data.data as TokenRow & { token: string },
    onSuccess: (d) => {
      setMinted(d.token);
      qc.invalidateQueries({ queryKey: ['api-tokens', orgId] });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-tokens', orgId] }),
  });

  const active = (tokens.data ?? []).filter((t) => !t.revokedAt);
  const isForbidden = (tokens.error as { response?: { status?: number } } | null)?.response?.status === 403;
  // Admins manage tokens; everyone else simply doesn't see the section.
  if (isForbidden) return null;

  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <KeyIcon color="primary" fontSize="small" />
        <Typography variant="h6">{c.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {c.bodyLead}{' '}
        <code>Authorization: Bearer rok_…</code>. {c.bodyTail}{' '}
        <a href="/api-docs" target="_blank" rel="noreferrer">/api-docs</a>. {c.bodySecret}
      </Typography>

      {minted && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMinted(null)}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <span style={{ wordBreak: 'break-all' }}>
              Copy it now. It will not be shown again:&nbsp;<code>{minted}</code>
            </span>
            <Button
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={async () => {
                await copyText(minted);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? c.copied : c.copy}
            </Button>
          </Stack>
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          label={c.tokenName}
          placeholder="ci-reporting"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select size="small" value={role} onChange={(e) => setRole(e.target.value as TokenRow['role'])}>
          <MenuItem value="VIEWER">{c.roleViewer}</MenuItem>
          <MenuItem value="MEMBER">{c.roleMember}</MenuItem>
          <MenuItem value="ADMIN">{c.roleAdmin}</MenuItem>
        </Select>
        <Button variant="contained" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
          Create token
        </Button>
      </Stack>

      {create.isError && <Alert severity="error" sx={{ mb: 2 }}>{c.createFailed}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{c.columns.name}</TableCell>
              <TableCell>{c.columns.token}</TableCell>
              <TableCell>{c.columns.role}</TableCell>
              <TableCell>{c.columns.lastUsed}</TableCell>
              <TableCell>{c.columns.created}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {active.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No server tokens yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {active.map((t) => (
              <TableRow key={t.id}>
                <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                <TableCell>
                  <code>{t.tokenPrefix}…</code>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={t.role} variant="outlined" />
                </TableCell>
                <TableCell>{t.lastUsedAt ? formatDateTime(t.lastUsedAt) : 'never'}</TableCell>
                <TableCell>{formatDateTime(t.createdAt)}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => revoke.mutate(t.id)}>
                    Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Quick start. Paste-ready calls; a just-minted token is filled in. */}
      <Paper sx={{ mt: 2, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Quick start
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {(['curl', 'js', 'python', 'mcp'] as const).map((l) => (
              <Chip
                key={l}
                size="small"
                clickable
                label={
                  l === 'js' ? 'JavaScript' : l === 'python' ? 'Python' : l === 'mcp' ? 'MCP' : 'curl'
                }
                color={lang === l ? 'primary' : 'default'}
                variant={lang === l ? 'filled' : 'outlined'}
                onClick={() => setLang(l)}
              />
            ))}
          </Stack>
          {!minted && (
            <Button
              size="small"
              variant="outlined"
              disabled={fillExample.isPending}
              onClick={() => fillExample.mutate()}
            >
              {fillExample.isPending ? c.filling : c.fillWithToken}
            </Button>
          )}
          <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
            onClick={async () => {
              await copyText(snippet(lang, window.location.origin, orgId ?? '', minted));
              setSnippetCopied(true);
              setTimeout(() => setSnippetCopied(false), 1500);
            }}
          >
            {snippetCopied ? c.copied : c.copy}
          </Button>
        </Stack>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontSize: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            overflowX: 'auto',
            whiteSpace: 'pre',
          }}
        >
          {snippet(lang, window.location.origin, orgId ?? '', minted)}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {minted
            ? c.snippetFilled
            : c.snippetEmpty}
          {!minted && (
            <a href="/api-docs" target="_blank" rel="noreferrer">
              /api-docs
            </a>
          )}
        </Typography>
      </Paper>
    </Box>
  );
}

/**
 * Assistants a person approved over MCP, and the way to take it back.
 *
 * Separate from API tokens because they are a different kind of credential:
 * a token is something an operator issued, a connection is something a person
 * approved on a consent screen. Listing them together would blur who decided.
 */
export function ConnectedAssistants() {
  const t = useT().oauth;
  const qc = useQueryClient();
  const grants = useQuery({
    queryKey: ['oauth-connections'],
    queryFn: async () =>
      (await api.get('/oauth/connections')).data.data as Array<{
        id: string;
        clientName: string;
        clientUri: string | null;
        role: string;
        createdAt: string;
        lastUsedAt: string | null;
      }>,
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => api.delete(`/oauth/connections/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-connections'] }),
  });

  const rows = grants.data ?? [];
  if (grants.isLoading) return null;
  // The endpoint is absent on a deployment that does not run the authorization
  // server, and most do not. Showing an empty state that invites somebody to
  // connect Slack would be inviting them to something that cannot work here.
  if (grants.isError) return null;
  // Otherwise rendered even when empty: somebody looking for this the first
  // time is looking for *where connections appear*, and a section that hides
  // itself until it has content answers that question by being absent.

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t.connections}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t.connectionsBody}
      </Typography>
      <Paper variant="outlined">
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t.noConnections}
          </Typography>
        ) : (
        <Table size="small">
          <TableBody>
            {rows.map((g) => (
              <TableRow key={g.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {g.clientName}
                  </Typography>
                  {g.clientUri && (
                    <Typography variant="caption" color="text.secondary">
                      {g.clientUri}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={g.role} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {t.lastUsed}: {g.lastUsedAt ? formatDateTime(g.lastUsedAt) : t.never}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => revoke.mutate(g.id)}>
                    {t.revoke}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </Paper>
    </Box>
  );
}
